/**
 * `BarsH` — horizontal bars, one or more series per row. The S2 Overview's two charts.
 *
 * The client drew this one. Their mockup of "Progress Against Targets" is a horizontal
 * grouped bar chart: countries down the Y axis, the value along X with ticks, two bars per
 * country (Target above Achieved), a hover popup naming the row and both numbers, and a
 * two-dot legend under the chart. This is that, in the portal's light theme rather than the
 * mockup's dark one, and it doubles as the pathway chart by being handed **one** series.
 *
 * It is a sibling of `BarsV`, not a mode of it. The two charts differ in every piece of
 * geometry they own — `BarsV` prints its category under the bar and its value above it and
 * draws no axis at all (§G8: "one axis per chart", and four printed numbers are that axis);
 * horizontal rows put the category in a left gutter, need a real X axis because a bar's
 * *length* is the only thing carrying the value, and stack N bars inside one row's band.
 * Folding both into one component would have been a prop that changes what every other prop
 * means.
 *
 * Three things it does that the mockup does not, each because this is a live page:
 *
 * 1. **A missing value draws no bar.** §D3 omits units with no data rather than reporting
 *    zero, so `null` gets an em dash where its number would be — a zero-length bar would
 *    claim the country reported nothing *because it achieved nothing*. This is the whole
 *    reason the targets chart can render at all before IFDC delivers: the Target series is
 *    all `null`, and the row is honest about it.
 * 2. **The hovered row highlights as a band**, not the single bar under the pointer. The
 *    mockup's popup names both of a country's numbers, so the thing being pointed at is the
 *    country, and highlighting half of it would be a different chart's behaviour.
 * 3. **A row is clickable** (`onSelect`), because S2 wants "click a bar → the map resizes to
 *    that country". Only when the caller passes a handler: without one the bars are not
 *    focusable and carry no pointer cursor, so the pathway chart does not pretend to be a
 *    control it is not.
 *
 * Hover goes through the shared `ChartTip` portal (§F2), like every other chart here.
 */
import { useId, useMemo, useState } from 'react';

import { EMDASH, fmtNum } from '../../lib/format';
import { hideChartTip, showChartTip } from './chartTipStore';

/** Left gutter for the row labels, and the bottom gutter the X axis lives in. */
export const BARSH_LABEL_GUTTER = 76;
/** Font size of a row label, and the width one character of it takes at that size. */
export const BARSH_LABEL_SIZE = 9;
export const BARSH_CHAR_RATIO = 0.52;
export const BARSH_AXIS_GUTTER = 15;
export const BARSH_PAD_TOP = 4;
export const BARSH_PAD_RIGHT = 10;
/** Vertical space between two rows, and between two bars inside one row. */
export const BARSH_ROW_GAP = 7;
export const BARSH_BAR_GAP = 2;
/** The mockup's `0 → 1M every 250K`: five gridlines including both ends. */
export const BARSH_TICKS = 5;
/** Headroom past the longest bar so its printed value is not clipped by the frame. */
export const BARSH_HEADROOM = 1.02;
/** How close a bar's end may come to the reference line before its number moves inside. */
export const BARSH_REF_CLEARANCE = 24;
/** How far below the head of a reference line its own label's baseline sits. */
export const BARSH_REF_LABEL_H = 9;

export interface BarsHSeries {
  /** Stable id — the `data-` hook and the React key. Never shown. */
  key: string;
  /** Legend/tooltip name, already translated (`Target`, `Achieved`, `Activities`). */
  label: string;
  color: string;
  /** One per row label; `null`/non-finite = not reported (no bar, em dash). */
  values: readonly (number | null | undefined)[];
  /**
   * Optional per-row override of `color`, positionally (§G8's fixed categorical order).
   *
   * Opt-in, and only ever used by a **single**-series chart: two series each painting their
   * rows differently would leave the legend describing nothing. It exists because a chart of
   * one series per category — the pathway bars — still wants each category in its own colour,
   * and splitting it into three one-row series would give each its own axis scale.
   */
  rowColors?: readonly string[];
}

export interface BarsHProps {
  /** One per row, top to bottom — countries, watersheds or pathway names. */
  labels: readonly string[];
  /** One entry = plain bars; two = the mockup's Target/Achieved pair. */
  series: readonly BarsHSeries[];
  w?: number;
  /** Height of one row's whole band, all its bars included. */
  rowH?: number;
  /**
   * Width of the left gutter the row labels sit in, and their font size.
   *
   * Both are props because the two charts on the Overview label their rows with different
   * kinds of word: `Mali` fits in the default 76 px and `Enabling Environment` — or, in
   * French, `Environnement favorable` — does not. A label too long for its gutter is
   * truncated with an ellipsis rather than allowed to run off the left edge of the viewBox,
   * where it is silently clipped by the frame; the full name is still in the hover popup and
   * in the caller's legend.
   */
  labelGutter?: number;
  labelSize?: number;
  /** Appended to every number in the tooltip (`44,858 ha`). */
  unit?: string;
  /** §G3 decimals. */
  fmt?: number;
  /** X gridlines, including 0 and the maximum. Fewer than 2 draws none. */
  ticks?: number;
  ariaLabel?: string;
  /** S2 "click a bar → the map resizes to that country". Omit and rows are inert. */
  onSelect?: (index: number, label: string) => void;
  className?: string;
  /**
   * A vertical marker across some of the rows — the targets chart's **programme reference
   * line** (client spec, 2026-09-09).
   *
   * IFDC's targets are a programme-wide commitment with no country allocation, so a country
   * row gets no target bar of its own. The line is how the reader still sees where the
   * commitment sits: it is drawn at `value`, dashed, from `fromRow` down, and it is
   * explicitly *not* a bar, because a bar on a country row would read as that country's
   * target and inventing one is the thing the specification forbids.
   *
   * It participates in the axis scale, so a line beyond every bar widens the plot rather
   * than falling off the right edge.
   */
  refLine?: { value: number; label: string; color?: string; fromRow?: number } | null;
  /**
   * **A composition instead of a comparison** — client, 15 Sep 2026: *"for the landcover we
   * can use a stacked bar plot to show all classes per country"*.
   *
   * One row is a whole (a country), the series are the parts inside it (the land-cover
   * classes), and the segments are laid end to end across the row rather than side by side.
   * The row is normalised to its **own** total, so every row fills the plot exactly and the
   * chart compares *shares* — which is the only comparison a composition supports, and the
   * reason there is no value axis in this mode: the axis is 100 %, and a gridline saying so
   * is a gridline earning nothing in a 250 px panel.
   *
   * A zero, a null and a negative are all "this row has none of that class" and draw no
   * segment. Unlike the grouped mode there is no em dash for them: a missing part of a whole
   * is already visible as the space the other parts took.
   *
   * `rowColors` still wins over `color` per row, and the per-segment popup names the series
   * and its share (`Forest · 25%`) under the row's own name.
   */
  stacked?: boolean;
  /**
   * Per-row fill opacity, positionally — the country comparison's "the filter is on some
   * other country" fade (see `InsightsPanel`'s `focusIso`).
   *
   * A row rather than a bar, because in a stack the thing being de-emphasised is the whole
   * country; painting one class of it differently would say something about the class.
   */
  rowOpacity?: readonly number[];
  /**
   * Multiplier on the longest bar (or the reference line) that sets the axis maximum.
   *
   * `BARSH_HEADROOM`'s 2 % is enough to keep a bar's printed value off the frame, which is
   * all the Overview's charts need. The dashboard's targets chart wants more: its axis is a
   * percentage with a 100 % reference line, and at 102 % the marker and the last tick are
   * pressed against the right edge (client, 2026-09-16 round 2).
   */
  headroom?: number;
  /**
   * Put each row's **untruncated** label on a native `<title>` over the label gutter.
   *
   * Opt-in, and off by default: every chart here already answers a hover through the shared
   * `ChartTip` portal, and a native tooltip on top of that is two popups for one pointer
   * (review, 2026-09-16). It earns its place only where the labels are long enough to be
   * ellipsised and the full text exists nowhere else on screen — the dashboard's targets
   * chart, whose rows are indicator names.
   */
  labelTitles?: boolean;
  /**
   * **The dashboard's softer bars** (client, 2026-09-16: "a modern look, faint colours").
   *
   * Opt-in, and off everywhere else on purpose: the Overview's and the insights panels'
   * charts are drawn in flat series colour, which is what makes a colour on one page the
   * same colour on the next. Where it is on, each bar is painted with a two-stop vertical
   * gradient of its *own* colour — full at the top, 75 % of it at the foot — and its ends
   * are rounded a pixel further. Nothing about the geometry, the scale or the numbers
   * changes; a `null` still draws no bar.
   */
  styled?: boolean;
}

/**
 * A compact axis label: `250K`, `1.2M`, `800` — the mockup's own register.
 *
 * Module-private on purpose. It is a property of this chart's axis, not a shared formatter,
 * and exporting it from a file that also exports a component costs a `react-refresh`
 * warning for no caller. It is asserted through the rendered tick text instead.
 */
function axisTick(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${Math.round((value / 1_000_000) * 10) / 10}M`;
  if (abs >= 1_000) return `${Math.round((value / 1_000) * 10) / 10}K`;
  return String(Math.round(value));
}

export function BarsH({
  labels,
  series,
  w = 290,
  rowH = 30,
  labelGutter = BARSH_LABEL_GUTTER,
  labelSize = BARSH_LABEL_SIZE,
  unit = '',
  fmt = 0,
  ticks = BARSH_TICKS,
  ariaLabel,
  onSelect,
  className,
  refLine = null,
  stacked = false,
  rowOpacity,
  headroom = BARSH_HEADROOM,
  labelTitles = false,
  styled = false,
}: BarsHProps) {
  const [hovered, setHovered] = useState<number | null>(null);
  /*
    One gradient per *colour*, not per bar: a chart of nine rows in one green would
    otherwise carry nine identical `<linearGradient>` elements. `useId` keeps the ids
    unique across two charts on one page; its own delimiters are stripped because the id
    is spliced into a `url(#…)` reference.
  */
  const rawId = useId();
  const gradients = useMemo(() => {
    if (!styled) return new Map<string, string>();
    const prefix = `bh${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const colors = new Set<string>();
    for (const s of series) {
      colors.add(s.color);
      for (const c of s.rowColors ?? []) colors.add(c);
    }
    return new Map([...colors].map((c, i) => [c, `${prefix}-${i}`]));
  }, [series, styled, rawId]);
  /** The bar's paint: its gradient where this chart has one, the flat colour otherwise. */
  const paint = (color: string) => {
    const id = gradients.get(color);
    return id ? `url(#${id})` : color;
  };

  const chart = useMemo(() => {
    const rows = labels.length;
    // A stack has no value axis, so it does not pay for the gutter one would live in.
    const height = BARSH_PAD_TOP + rows * rowH + (stacked ? BARSH_ROW_GAP : BARSH_AXIS_GUTTER);
    const plotW = Math.max(10, w - labelGutter - BARSH_PAD_RIGHT);
    const numbers = series.map((s) =>
      labels.map((_, i) => {
        const v = s.values[i];
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
      }),
    );
    const marker =
      refLine && Number.isFinite(refLine.value) && refLine.value > 0 ? refLine.value : 0;
    // The marker is part of the scale, not drawn over it: a programme commitment larger than
    // every country's achievement would otherwise sit off the right edge of the frame.
    const peak = Math.max(0, marker, ...numbers.flat().map((v) => v ?? 0));
    // `peak > 0` guards the `v / 0` that is the difference between an empty chart and a
    // chart of NaN path data — which is exactly the state the targets chart opens in.
    const max = peak > 0 ? peak * headroom : 1;
    const bandH = rowH - BARSH_ROW_GAP;
    const barH = Math.max(3, (bandH - BARSH_BAR_GAP * (series.length - 1)) / series.length);

    return {
      height,
      plotW,
      max,
      peak,
      bars: labels.map((label, row) => ({
        label,
        row,
        y: BARSH_PAD_TOP + row * rowH,
        bandH,
        /**
         * The stacked row: each series' share of *this row's* total, laid end to end.
         *
         * Normalising per row rather than against the chart's peak is what makes the row a
         * whole. `total` is the sum of the positive values only, so a row whose parts sum to
         * 99.8 after the server's rounding still fills the plot exactly rather than leaving
         * a sliver of background that reads as an unnamed class.
         */
        segments: (() => {
          if (!stacked) return [];
          const parts = series
            .map((s, si) => ({ s, v: numbers[si]?.[row] ?? null }))
            .filter((p): p is { s: BarsHSeries; v: number } => p.v !== null && p.v > 0);
          const total = parts.reduce((sum, p) => sum + p.v, 0);
          if (total <= 0) return [];
          let x = labelGutter;
          return parts.map((p) => {
            const width = (p.v / total) * plotW;
            const seg = { series: p.s, v: p.v, x, width, pct: (p.v / total) * 100 };
            x += width;
            return seg;
          });
        })(),
        values: series.map((s, si) => {
          const v = numbers[si]?.[row] ?? null;
          return {
            series: s,
            v,
            y: BARSH_PAD_TOP + row * rowH + si * (barH + BARSH_BAR_GAP),
            h: barH,
            len: v === null ? 0 : Math.max(0, (v / max) * plotW),
          };
        }),
      })),
    };
  }, [labels, series, w, rowH, labelGutter, refLine, stacked, headroom]);

  const text = (v: number | null | undefined) =>
    v === null || v === undefined ? EMDASH : `${fmtNum(v, fmt)}${unit ? ` ${unit}` : ''}`;

  /** The mockup's popup: `Nigeria` over `Target 800K · Achieved 612K`, in one tip. */
  const tipFor = (row: number) =>
    stacked
      ? (chart.bars[row]?.segments ?? [])
          .map((seg) => `${seg.series.label} ${fmtNum(seg.pct, fmt)}%`)
          .join(' · ')
      : (chart.bars[row]?.values ?? []).map((b) => `${b.series.label} ${text(b.v)}`).join(' · ');

  /**
   * One segment's popup: the class and its share of the row (`Forest · 25%`).
   *
   * A stacked row's whole composition can be eight classes long, so naming all of them in
   * one tip — what the grouped mode does — would print a paragraph over the map. The reader
   * is pointing at one class; the tip answers about that class.
   */
  const segmentTip = (label: string, pct: number) => `${label} · ${fmtNum(pct, fmt)}%`;

  const enter = (e: { clientX: number; clientY: number }, row: number) => {
    setHovered(row);
    showChartTip(e, labels[row] ?? '', tipFor(row));
  };

  /**
   * The same popup, placed for a row that was reached by tabbing rather than pointed at.
   *
   * `showChartTip` positions from a cursor, and a keyboard has none — so the row's own box
   * stands in for it, which puts the tip beside the row the way the pointer would.
   */
  const focusRow = (node: SVGGElement, row: number) => {
    const box = node.getBoundingClientRect();
    enter({ clientX: box.right, clientY: box.top + box.height / 2 }, row);
  };
  const leave = () => {
    setHovered(null);
    hideChartTip();
  };

  /**
   * A label that fits its gutter, or the start of one plus an ellipsis.
   *
   * Measured arithmetically rather than with `getComputedTextLength`, which needs a laid-out
   * SVG and returns 0 in jsdom — this has to be right in a unit test and during the first
   * render, not only once the browser has painted.
   */
  const fitLabel = (label: string) => {
    const max = Math.max(3, Math.floor((labelGutter - 6) / (labelSize * BARSH_CHAR_RATIO)));
    return label.length <= max ? label : `${label.slice(0, max - 1).trimEnd()}…`;
  };

  /** Where the reference line stands, or `null` when this chart has none. */
  const refX =
    refLine && Number.isFinite(refLine.value) && refLine.value > 0
      ? labelGutter + (refLine.value / chart.max) * chart.plotW
      : null;

  // A composition's axis is 100 % — there is nothing for a gridline to say (see `stacked`).
  const tickValues =
    ticks >= 2 && !stacked
      ? Array.from({ length: ticks }, (_, i) => (chart.max / (ticks - 1)) * i)
      : [];

  return (
    <svg
      className={`chart barsh${className ? ` ${className}` : ''}`}
      width="100%"
      viewBox={`0 0 ${w} ${chart.height}`}
      role="img"
      aria-label={ariaLabel ?? `${labels.length} rows`}
      data-rows={labels.length}
      data-series={series.length}
      onMouseLeave={leave}
    >
      {gradients.size > 0 ? (
        <defs>
          {[...gradients].map(([color, id]) => (
            <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={1} />
              <stop offset="100%" stopColor={color} stopOpacity={0.75} />
            </linearGradient>
          ))}
        </defs>
      ) : null}

      {/* Gridlines first, so every bar is drawn over them rather than under. */}
      {tickValues.map((value, i) => {
        const x = labelGutter + (value / chart.max) * chart.plotW;
        return (
          <g key={`t${i}`} data-barsh-tick={i}>
            <line
              x1={x}
              x2={x}
              y1={BARSH_PAD_TOP}
              y2={chart.height - BARSH_AXIS_GUTTER}
              stroke="#E4E9E2"
              strokeWidth={1}
            />
            <text
              x={x}
              y={chart.height - 4}
              textAnchor={i === 0 ? 'start' : i === tickValues.length - 1 ? 'end' : 'middle'}
              fontSize={8}
              fill="#8B958A"
            >
              {axisTick(value)}
            </text>
          </g>
        );
      })}

      {/* The programme reference line, over the gridlines and under the bars' numbers. */}
      {refX !== null && refLine
        ? (() => {
            const from = Math.max(0, refLine.fromRow ?? 0);
            const top = BARSH_PAD_TOP + from * rowH;
            return (
              <g>
                <line
                  data-barsh-refline={refLine.value}
                  data-label={refLine.label}
                  x1={refX}
                  x2={refX}
                  y1={top}
                  y2={chart.height - BARSH_AXIS_GUTTER}
                  stroke={refLine.color ?? '#1E5FA8'}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                >
                  <title>{refLine.label}</title>
                </line>
                {/*
                  The line's own words, at its head. The label used to live only in a
                  `<title>`, so a reader who never hovered was shown a dashed line with
                  nothing saying what it marks (review, 2026-09-16). It hangs to whichever
                  side has room, so it is never clipped by the frame.
                */}
                <text
                  data-barsh-reflabel={refLine.value}
                  x={refX + (refX > w * 0.7 ? -4 : 4)}
                  y={top + BARSH_REF_LABEL_H}
                  textAnchor={refX > w * 0.7 ? 'end' : 'start'}
                  fontSize={8}
                  fontWeight={700}
                  fill={refLine.color ?? '#1E5FA8'}
                >
                  {refLine.label}
                </text>
              </g>
            );
          })()
        : null}

      {chart.bars.map((row) => (
        /*
         * **A selectable row is reachable from the keyboard, and so is its popup.** When
         * `onSelect` is set, clicking a row moves the map (S2 item 8), which made the whole
         * gesture mouse-only — and the popup naming the row's two numbers was the only place
         * they are written down, so a reader who cannot point had neither the action nor the
         * values. Focus does what hover does and Enter/Space does what a click does; the row
         * announces itself as a button because that is what it now is.
         *
         * A row that selects nothing stays inert: it gets no tab stop, because a tab stop
         * that does nothing is worse than none.
         */
        <g
          key={row.label}
          data-barsh-row={row.row}
          data-label={row.label}
          data-hovered={hovered === row.row ? '1' : undefined}
          style={onSelect ? { cursor: 'pointer' } : undefined}
          tabIndex={onSelect ? 0 : undefined}
          role={onSelect ? 'button' : undefined}
          aria-label={onSelect ? `${row.label} — ${tipFor(row.row)}` : undefined}
          onMouseEnter={stacked ? undefined : (e) => enter(e, row.row)}
          onMouseMove={stacked ? undefined : (e) => enter(e, row.row)}
          onMouseLeave={leave}
          onFocus={onSelect ? (e) => focusRow(e.currentTarget, row.row) : undefined}
          onBlur={onSelect ? leave : undefined}
          onKeyDown={
            onSelect
              ? (e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  onSelect(row.row, row.label);
                }
              : undefined
          }
          onClick={onSelect ? () => onSelect(row.row, row.label) : undefined}
        >
          {/* The highlight band. Always rendered so the row has one hit target covering the
              label gutter, the bars and the space between them — a reader aiming at a 5px
              bar in a 300px panel should not have to. */}
          <rect
            x={0}
            y={row.y - BARSH_ROW_GAP / 2}
            width={w}
            height={row.bandH + BARSH_ROW_GAP}
            fill={hovered === row.row ? 'rgba(46,125,50,.08)' : 'transparent'}
            data-barsh-band={row.row}
          />
          <text
            x={labelGutter - 6}
            y={row.y + row.bandH / 2 + 3}
            textAnchor="end"
            fontSize={labelSize}
            fill="#22301F"
            data-barsh-label={row.row}
            data-label-full={row.label}
          >
            {fitLabel(row.label)}
          </text>
          {/* The label gutter's own hit area, carrying the *untruncated* name as a native
              tooltip: `fitLabel` above ellipsises anything longer than the gutter, and a
              reader meeting "B.1.1 Producers increasing net in…" needs some way to read the
              rest. Only where the caller asked for it (`labelTitles`) — see the prop.
              Transparent and event-transparent to the row `<g>` above, so the row highlight
              and the shared `ChartTip` behave exactly as before. */}
          {labelTitles ? (
            <rect
              x={0}
              y={row.y - BARSH_ROW_GAP / 2}
              width={labelGutter}
              height={row.bandH + BARSH_ROW_GAP}
              fill="transparent"
              data-barsh-label-hit={row.row}
            >
              <title>{row.label}</title>
            </rect>
          ) : null}
          {stacked
            ? row.segments.map((seg) => (
                <rect
                  key={seg.series.key}
                  data-barsh-segment={`${row.row}:${seg.series.key}`}
                  data-label={seg.series.label}
                  data-value={segmentTip(seg.series.label, seg.pct)}
                  x={seg.x}
                  y={row.y}
                  width={seg.width}
                  height={row.bandH}
                  fill={paint(seg.series.rowColors?.[row.row] ?? seg.series.color)}
                  opacity={
                    (rowOpacity?.[row.row] ?? 1) *
                    (hovered === null || hovered === row.row ? 1 : 0.55)
                  }
                  onMouseEnter={(e) => {
                    setHovered(row.row);
                    showChartTip(e, row.label, segmentTip(seg.series.label, seg.pct));
                  }}
                  onMouseMove={(e) => {
                    setHovered(row.row);
                    showChartTip(e, row.label, segmentTip(seg.series.label, seg.pct));
                  }}
                  onMouseLeave={leave}
                />
              ))
            : null}
          {stacked ? null : row.values.map((bar) => {
            const end = labelGutter + bar.len;
            /*
              Print the number past the bar's end, flipped **inside** the bar when there is
              no room for it out there: off the right edge of the frame (a chart in a 20 %
              panel has no room to lose a digit), or on top of the reference line, where
              "97" and a dashed 100 % marker were printing over each other (review,
              2026-09-16). Inside the bar the number is white on the bar's own fill, which
              is where a bar that long has the most room anyway.
            */
            const collides =
              refX !== null && bar.v !== null && Math.abs(refX - end) < BARSH_REF_CLEARANCE;
            const overflows = end + 30 > w || collides;
            return (
              <g key={bar.series.key} data-barsh-series={bar.series.key}>
                {bar.v === null ? null : (
                  <rect
                    x={labelGutter}
                    y={bar.y}
                    width={bar.len}
                    height={bar.h}
                    rx={styled ? 3 : 2}
                    fill={paint(bar.series.rowColors?.[row.row] ?? bar.series.color)}
                    opacity={
                      (rowOpacity?.[row.row] ?? 1) *
                      (hovered === null || hovered === row.row ? 1 : 0.55)
                    }
                    data-value={text(bar.v)}
                  />
                )}
                <text
                  x={overflows ? end - 3 : end + 3}
                  y={bar.y + bar.h / 2 + 3}
                  textAnchor={overflows ? 'end' : 'start'}
                  fontSize={8}
                  fontWeight={700}
                  fill={overflows ? '#FFFFFF' : '#22301F'}
                  data-barsh-value={`${row.row}:${bar.series.key}`}
                >
                  {bar.v === null ? EMDASH : fmtNum(bar.v, fmt)}
                </text>
              </g>
            );
          })}
        </g>
      ))}
    </svg>
  );
}

export default BarsH;
